import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <title>Privacy Policy — Practicio</title>
        <meta name="description" content="Practicio's Privacy Policy. Learn how we collect, use, store, and protect the information of architects and interior designers who use our platform." />
        <link rel="canonical" href="https://practicio.io/privacy" />
      </Helmet>
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors mb-10" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-700 mb-2" data-testid="privacy-heading">Privacy Policy</h1>
          <p className="text-sm text-stone-400 mb-10">Last updated: February 2026</p>

          <div className="space-y-8 text-stone-600 leading-relaxed">
            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">1. Introduction</h2>
              <p>
                Practicio ("we", "our", "us") is committed to protecting the privacy of architects, interior designers, and other professionals who use our platform. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website and services.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">2. Information We Collect</h2>
              <p><strong>Account Information:</strong> When you sign up, we collect your name, email address, company name, role, and contact details.</p>
              <p><strong>Project Data:</strong> Information you enter about your projects, including client details, approvals, BOQs, scope changes, and billing milestones.</p>
              <p><strong>Usage Data:</strong> We collect information about how you interact with our platform, including pages visited, features used, and session duration.</p>
              <p><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers for security and analytics purposes.</p>
              <p><strong>Communications:</strong> When you contact us via email or our contact form, we retain the content of those communications.</p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide, maintain, and improve our platform and services</li>
                <li>Process your transactions and manage your account</li>
                <li>Send service-related communications, updates, and security alerts</li>
                <li>Respond to your enquiries and provide customer support</li>
                <li>Analyse usage patterns to improve user experience</li>
                <li>Ensure the security and integrity of our platform</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">4. Data Storage and Security</h2>
              <p>
                Your data is stored on secure cloud servers. We implement industry-standard security measures including encryption in transit and at rest, access controls, and regular security audits. We do not sell, rent, or trade your personal information to third parties.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">5. Data Sharing</h2>
              <p>We may share your information only in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Service Providers:</strong> With trusted third-party services that help us operate our platform (e.g., hosting, analytics, payment processing)</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorise us to share your data</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (subject to legal retention requirements)</li>
                <li>Export your data in a portable format</li>
                <li>Withdraw consent for data processing where applicable</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">7. Data Retention</h2>
              <p>
                We retain your data for as long as your account is active or as needed to provide our services. If you close your account, we will delete or anonymise your data within 90 days, except where retention is required by law.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">8. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and, where appropriate, by email.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">9. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or your data, contact us at{' '}
                <a href="mailto:hello@practicio.io" className="text-slate-600 hover:text-slate-700 font-medium underline underline-offset-2">
                  hello@practicio.io
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
